import React, { createContext, useContext, useReducer, useEffect } from 'react';

export const INITIAL = { layouts: [], editMode: false, saved: false, dashboardId: null, isTemplate: false };

export const A = {
  ADD_LAYOUT: 'ADD_LAYOUT',
  REMOVE_LAYOUT: 'REMOVE_LAYOUT',
  RENAME_LAYOUT: 'RENAME_LAYOUT',
  SET_SLOT: 'SET_SLOT',
  REORDER: 'REORDER',
  ENTER_EDIT: 'ENTER_EDIT',
  EXIT_EDIT: 'EXIT_EDIT',
  SAVE: 'SAVE',
  RESET: 'RESET',
  HYDRATE: 'HYDRATE',
  APPLY_TEMPLATE: 'APPLY_TEMPLATE',
};

let _id = 1;
const uid = () => `lay_${Date.now()}_${_id++}`;

function baseReducer(state, { type, payload }) {
  switch (type) {
    case A.HYDRATE:
      return { ...INITIAL, ...payload, editMode: false };

    // Puts the default template in the editor as a pending change. The saved dashboard (and its id) stay as they are
    // until the user saves, and discarding goes back to what was saved.
    case A.APPLY_TEMPLATE:
      return { ...state, layouts: payload.layouts, saved: false, editMode: true, isTemplate: false };

    case A.ADD_LAYOUT: {
      const { cols, slots, name, layoutType, bentoTemplateId } = payload;
      const n = state.layouts.length + 1;
      return {
        ...state, saved: false,
        editMode: true,          // ← auto-enter edit mode on new layout
        layouts: [...state.layouts, {
          id: uid(),
          name: name || `Bloque ${n}`,
          layoutType: layoutType || 'columns',
          cols: cols || 2,
          bentoTemplateId: bentoTemplateId || null,
          order: n,
          slots: slots || Array.from({ length: cols || 2 }, () => null),
        }],
      };
    }

    case A.REMOVE_LAYOUT:
      return {
        ...state,
        layouts: state.layouts
          .filter(layout => (layout.id || layout._id) !== payload)
          .map((layout, index) => ({
            ...layout,
            order: index + 1,
          })),
        saved: false,
      };

    case A.RENAME_LAYOUT:
      return {
        ...state, saved: false,
        layouts: state.layouts.map(l =>
          l.id === payload.id ? { ...l, name: payload.name } : l
        ),
      };

    case A.SET_SLOT: {
      const { layoutId, slotIdx, data } = payload;
      return {
        ...state, saved: false,
        layouts: state.layouts.map(l =>
          l.id !== layoutId ? l : {
            ...l,
            slots: l.slots.map((s, i) => i === slotIdx ? { ...(s || {}), ...data } : s),
          }
        ),
      };
    }

    case A.REORDER: {
      const { fromIdx, toIdx } = payload;
      const arr = [...state.layouts];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return { ...state, saved: false, layouts: arr };
    }

    case A.ENTER_EDIT:
      return { ...state, editMode: true };

    case A.EXIT_EDIT:
      return { ...state, editMode: false };

    case A.SAVE:
      return { ...state, saved: true };

    case A.RESET:
      return { ...INITIAL };

    default:
      return state;
  }
}

// Changing the default template, or saving it, turns it into the user's own dashboard
const CHANGES_THAT_END_THE_TEMPLATE = new Set([A.ADD_LAYOUT, A.REMOVE_LAYOUT, A.RENAME_LAYOUT, A.SET_SLOT, A.REORDER, A.SAVE]);

export function reducer(state, action) {
  const next = baseReducer(state, action);
  if (next.isTemplate && CHANGES_THAT_END_THE_TEMPLATE.has(action.type)) return { ...next, isTemplate: false };
  return next;
}

const Ctx = createContext(null);
const KEY = 'ov5_dashboard';

const readStorageJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const dashboardStorageKey = () => {
  const user = readStorageJSON('user');
  const business = readStorageJSON('business');
  const businessCode = business?.code || user?.business?.code || 'business';
  const userCode = user?.user_code || user?.id || 'user';
  return `${KEY}_${businessCode}_${userCode}`;
};

export function DashboardProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(dashboardStorageKey());
      if (raw) dispatch({ type: A.HYDRATE, payload: JSON.parse(raw) });
    } catch (_) { }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(dashboardStorageKey(), JSON.stringify({ layouts: state.layouts, saved: state.saved }));
    } catch (_) { }
  }, [state.layouts, state.saved]);

  return <Ctx.Provider value={{ state, dispatch }}>{children}</Ctx.Provider>;
}

export const useDashboard = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDashboard requires DashboardProvider');
  return ctx;
};
